import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import * as magazzinoApi from "../../api/magazzino";
import { OrdineMagazzino, UbicazioneMagazzino, UBICAZIONE_LABELS, FornitoreOption } from "../../api/magazzino";
import * as anagraficaApi from "../../api/anagrafica";
import { CentroAssistenzaOption } from "../../api/anagrafica";
import * as clientsApi from "../../api/clients";
import { SedeCliente } from "../../api/clients";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";

/**
 * Sposta la merce di una riga d'ordine, e dice DOVE esattamente.
 *
 * ## Un campo per ubicazione, e compare solo per la sua
 *
 * `ubicazione` dice il tipo di posto; tre valori su quattro vogliono anche il
 * posto preciso. Il campo corrispondente **non esiste** finché quell'ubicazione
 * non è scelta: non si disabilita, proprio non c'è. Mostrarlo spento sulle
 * altre suggerirebbe che esista e che manchi qualcosa.
 *
 * | ubicazione          | campo               | obbligatorio?                   |
 * |---------------------|---------------------|---------------------------------|
 * | `presso_fornitore`  | fornitore           | sì, se sposti qui deliberatamente |
 * | `nostro_magazzino`  | —                   | il posto è uno solo             |
 * | `centro_assistenza` | centro              | sempre                          |
 * | `presso_cliente`    | sede del cliente    | solo se il cliente ne ha        |
 *
 * ## Le due eccezioni, e perché non sono arbitrarie
 *
 * **Il fornitore si chiede solo qui, mai alla creazione**: `presso_fornitore` è
 * il default di un ordine nuovo, e pretenderlo lì bloccherebbe la creazione di
 * qualunque ordine — al 25/08/2026 i contatti classificati come fornitore sono
 * zero su 480.
 *
 * **La sede si chiede solo se il cliente ne ha almeno una**: 456 clienti su 480
 * non hanno nessuna sede in anagrafica (arrivano dalla sync dei contatti figli
 * di Odoo). Un obbligo secco avrebbe reso impossibile marcare come consegnato
 * il 95% degli ordini. Se ne ha **una sola** — 18 clienti su 24 — viene
 * preselezionata: non c'è niente da scegliere, e chiederlo sarebbe un clic
 * inutile su una tendina con una voce.
 *
 * Il controllo vero resta nel backend (`utils/ordineMagazzino.ts`): qui viene
 * anticipato per aiutare a compilare, non riscritto.
 *
 * ## La sede proposta dall'installazione
 *
 * Un cliente GDO ha centinaia di sedi (Scarpe & Scarpe 137 al 04/11/2026), e
 * cercare a mano in una tendina quella giusta è il modo più sicuro di
 * sbagliarla. Se i pezzi dell'ordine sono collegati a un'installazione che
 * indica una sede, e la sede è una sola e del cliente dell'ordine, scegliendo
 * «Consegnata al cliente» la si trova già selezionata. È una proposta: si
 * cambia come qualunque altra scelta, e una volta cambiata non torna. Senza
 * installazione, o con pezzi installati in sedi diverse, la tendina resta vuota
 * come prima: lì non c'è una risposta da proporre.
 */
export function CambiaUbicazione({
  ordine,
  onChiudi,
  onFatto,
}: {
  ordine: OrdineMagazzino;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const { showToast } = useToast();
  const [ubicazione, setUbicazione] = useState<UbicazioneMagazzino>(ordine.ubicazione);
  const [centroId, setCentroId] = useState(ordine.centro_assistenza?.id ?? "");
  const [fornitoreId, setFornitoreId] = useState(ordine.fornitore?.id ?? "");
  const [sedeId, setSedeId] = useState(ordine.sede_cliente?.id ?? "");

  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);
  const [fornitori, setFornitori] = useState<FornitoreOption[]>([]);
  const [sedi, setSedi] = useState<SedeCliente[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  /** Il cliente dell'ordine, se è uno vero in anagrafica. Un "cliente nuovo" non ha sedi: non esiste ancora. */
  const clienteId = ordine.cliente_preassegnato?.id ?? null;

  /**
   * La sede dove l'installazione ha montato i pezzi di questa riga, se è una
   * sola. Con pezzi in sedi diverse non c'è una risposta da proporre: `null`.
   */
  const sediInstallazione = new Set(
    ordine.unita.flatMap((u) => u.device.installazioni.map((i) => i.sede_id)).filter((s): s is string => Boolean(s))
  );
  const sedeDaInstallazione = sediInstallazione.size === 1 ? [...sediInstallazione][0] : null;
  /** La proposta si fa una volta: chi poi svuota o cambia la sede non se la vede rimettere. */
  const propostaFatta = useRef(false);

  useEffect(() => {
    // Tre elenchi indipendenti: quello che non si carica lascia il suo campo
    // senza opzioni, e il backend rifiuterà con un messaggio chiaro. Nessuno
    // dei tre deve bloccare il pannello.
    anagraficaApi
      .listCentriAssistenza()
      .then(({ centri: c }) => setCentri(c))
      .catch(() => setCentri([]));
    magazzinoApi
      .listFornitori()
      .then(({ fornitori: f }) => setFornitori(f))
      .catch(() => setFornitori([]));
    if (clienteId) {
      clientsApi
        .listSediByCliente(clienteId)
        .then(({ sedi: s }) => {
          setSedi(s);
          // Una sola sede: si preseleziona. Non è una comodità, è togliere una
          // scelta che non esiste — ed è il caso di 18 clienti su 24.
          if (s.length === 1 && !sedeId) setSedeId(s[0].id);
        })
        .catch(() => setSedi([]));
    } else {
      setSedi([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const serveCentro = ubicazione === "centro_assistenza";
  const serveFornitore = ubicazione === "presso_fornitore";
  const serveSede = ubicazione === "presso_cliente";

  // Appena «Consegnata al cliente» è scelta e le sedi del cliente sono
  // arrivate: se l'installazione indica una sede di QUESTO cliente e non ce
  // n'è già una scelta, la si propone. Il controllo sull'appartenenza lo
  // rifarebbe comunque il backend; qui evita di selezionare un valore che la
  // tendina non sa mostrare.
  useEffect(() => {
    if (propostaFatta.current || !serveSede || sedi === null || !sedeDaInstallazione) return;
    propostaFatta.current = true;
    if (!sedeId && sedi.some((s) => s.id === sedeDaInstallazione)) setSedeId(sedeDaInstallazione);
  }, [serveSede, sedi, sedeDaInstallazione, sedeId]);
  /** Il cliente ha sedi fra cui scegliere? Finché l'elenco non è arrivato, non si sa. */
  const clienteHaSedi = (sedi?.length ?? 0) > 0;
  /** Il fornitore è obbligatorio solo se questo pannello sta spostando la merce lì. */
  const cambioAFornitore = serveFornitore && ordine.ubicazione !== "presso_fornitore";

  async function salva(e: React.FormEvent) {
    e.preventDefault();
    if (serveCentro && !centroId) {
      setErrore("Indica presso quale centro assistenza si trova la merce.");
      return;
    }
    if (cambioAFornitore && !fornitoreId) {
      setErrore("Indica presso quale fornitore si trova la merce.");
      return;
    }
    if (serveSede && clienteHaSedi && !sedeId) {
      setErrore("Indica a quale sede del cliente è stata consegnata la merce.");
      return;
    }

    setErrore(null);
    setInCorso(true);
    try {
      // I tre campi si mandano SEMPRE, espliciti: quelli che l'ubicazione non
      // prevede vanno a `null`, o resterebbero appesi a uno stato che non li
      // ammette. Il backend li azzera comunque, ma dirlo qui rende il payload
      // leggibile a chi guarda la chiamata.
      await magazzinoApi.aggiornaOrdine(ordine.id, {
        ubicazione,
        centro_assistenza_id: serveCentro ? centroId || null : null,
        fornitore_id: serveFornitore ? fornitoreId || null : null,
        sede_id: serveSede ? sedeId || null : null,
      });
      showToast(`Merce spostata: ${UBICAZIONE_LABELS[ubicazione].toLowerCase()}.`, "success");
      onFatto();
      onChiudi();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile cambiare l'ubicazione."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open
      onClose={onChiudi}
      title="Dove si trova la merce"
      size="sm"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="submit" form="form-ubicazione" loading={inCorso}>
            <MapPin className="h-4 w-4" aria-hidden="true" />
            Sposta
          </Button>
        </div>
      }
    >
      <form id="form-ubicazione" onSubmit={salva} className="flex flex-col gap-4" noValidate>
        <p className="text-sm text-ink/70">
          Ordine{" "}
          {ordine.odoo_order_ref ? (
            <span className="font-semibold text-ink">{ordine.odoo_order_ref}</span>
          ) : (
            "senza riferimento"
          )}{" "}
          — {ordine.quantita} {ordine.quantita === 1 ? "pezzo" : "pezzi"}.
        </p>

        <div className="flex flex-col gap-2">
          {(Object.keys(UBICAZIONE_LABELS) as UbicazioneMagazzino[]).map((u) => (
            <label key={u} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="radio"
                name="ubicazione"
                value={u}
                checked={ubicazione === u}
                onChange={() => {
                  setUbicazione(u);
                  setErrore(null);
                }}
                className="h-4 w-4 accent-primary"
              />
              {UBICAZIONE_LABELS[u]}
              {u === "presso_cliente" && <span className="text-xs text-ink/45">— non conta più come disponibile</span>}
            </label>
          ))}
        </div>

        {serveCentro && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ubi-centro" className="text-sm font-medium text-ink">
              Presso quale centro
            </label>
            <select
              id="ubi-centro"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              value={centroId}
              onChange={(e) => {
                setCentroId(e.target.value);
                setErrore(null);
              }}
            >
              <option value="">Scegli un centro…</option>
              {centri.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>
        )}

        {serveFornitore && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ubi-fornitore" className="text-sm font-medium text-ink">
              Presso quale fornitore
            </label>
            <select
              id="ubi-fornitore"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              value={fornitoreId}
              onChange={(e) => {
                setFornitoreId(e.target.value);
                setErrore(null);
              }}
            >
              <option value="">{cambioAFornitore ? "Scegli un fornitore…" : "Non indicato"}</option>
              {fornitori.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                </option>
              ))}
            </select>
            {fornitori.length === 0 && (
              <p className="text-xs text-ink/50">
                Nessun contatto è classificato come fornitore in anagrafica. Si imposta dalla scheda del contatto, campo
                «Tipo contatto».
              </p>
            )}
          </div>
        )}

        {serveSede && (
          <div className="flex flex-col gap-1.5">
            {sedi === null ? (
              <p className="text-sm text-ink/50">Cerco le sedi del cliente…</p>
            ) : !clienteId ? (
              <p className="text-xs text-ink/50">
                L&apos;ordine è intestato a un cliente non ancora in anagrafica, quindi non ha sedi fra cui scegliere. La
                consegna si registra lo stesso.
              </p>
            ) : clienteHaSedi ? (
              <>
                <label htmlFor="ubi-sede" className="text-sm font-medium text-ink">
                  A quale sede di {ordine.cliente_preassegnato?.nome}
                </label>
                <select
                  id="ubi-sede"
                  className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                  value={sedeId}
                  onChange={(e) => {
                    setSedeId(e.target.value);
                    setErrore(null);
                  }}
                >
                  <option value="">Scegli una sede…</option>
                  {sedi.map((s) => (
                    <option key={s.id} value={s.id}>
                      {[s.nome_sede, s.citta].filter(Boolean).join(" · ") || "Sede senza nome"}
                    </option>
                  ))}
                </select>
                {sedeDaInstallazione && sedeId === sedeDaInstallazione && (
                  <p className="text-xs text-ink/50">
                    Proposta dall&apos;installazione collegata a questi pezzi: se non è quella giusta, scegline un&apos;altra.
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs text-ink/50">
                {ordine.cliente_preassegnato?.nome} non ha sedi in anagrafica: la consegna si registra senza indicarne
                una. Le sedi arrivano dalla sincronizzazione con Odoo.
              </p>
            )}
          </div>
        )}

        {ordine.quantita > 1 && (
          <p className="rounded-lg border border-line bg-line/10 p-3 text-xs leading-relaxed text-ink/60">
            L&apos;ubicazione vale per tutta la riga d&apos;ordine, non per i singoli pezzi: spostandola, tutti e{" "}
            {ordine.quantita} risultano lì. Un ordine consegnato a metà oggi non si sa raccontare — se serve, va spezzato
            in due ordini.
          </p>
        )}

        {errore && <p className="text-sm font-medium text-accent">{errore}</p>}
      </form>
    </Modal>
  );
}
